import { classifyRequest, rankForRequest } from "@qn/agent-core";
import { Button, EmptyState, Logo, Spinner } from "@qn/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, PanelLeft, Plus, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApprovalBar, TaskPanel } from "../components/AgentParts";
import { AgentPicker } from "../components/AgentPicker";
import { ArtifactPanel } from "../components/ArtifactPanel";
import { Composer } from "../components/Composer";
import {
  AssistantMessage,
  LiveAssistantMessage,
  type ToolInfo,
  UserMessage,
} from "../components/MessageView";
import { ModelPicker } from "../components/ModelPicker";
import { ModePicker } from "../components/ModePicker";
import { RealtimeVoice } from "../components/RealtimeVoice";
import { VoiceMode } from "../components/VoiceMode";
import { cancelRun, decide, follow, resume, startAgent, useAgentStore } from "../lib/agent-store";
import { useProjects } from "../lib/agents";
import { api, type Conversation, type Message, type PickerModel, textOf } from "../lib/api";
import { runTurn, stopStream, useChatStore } from "../lib/chat-store";
import { conversationToText, downloadText, safeFilename } from "../lib/export";
import { useFeature } from "../lib/features";
import { useT } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";
import { useSettingsQuery } from "../lib/settings";
import { useUI } from "../lib/ui-store";
import {
  chunkForSpeech,
  createSpeaker,
  DEFAULT_VOICE,
  fetchVoiceCaps,
  type Speaker,
  unlockAudio,
} from "../lib/voice";

const isToolResultOnly = (m: Message) =>
  m.role === "user" && m.content.length > 0 && m.content.every((p) => p.type === "tool_result");

const greetKey = (h: number) =>
  h < 5 ? "greet.night" : h < 12 ? "greet.morning" : h < 18 ? "greet.afternoon" : "greet.evening";

export function ChatPage() {
  const t = useT();
  const agentOn = useFeature("agent");
  const suggestionsOn = useFeature("suggestions");
  const { id } = useParams();
  const [search] = useSearchParams();
  const newProjectId = search.get("project");
  const projects = useProjects().data?.projects ?? [];
  const artifactId = useUI((u) => u.artifactId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { lastModel, set, sidebarOpen, agentMode, lastAgentId, sendMode, autoModel } = usePrefs();
  const [think, setThink] = useState(false);
  const [plan, setPlan] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const voiceCaps = useQuery({
    queryKey: ["voice-caps"],
    queryFn: fetchVoiceCaps,
    staleTime: 60_000,
  });
  const [prefill, setPrefill] = useState<{ text: string; nonce: number } | null>(null);

  const models = useQuery({
    queryKey: ["models"],
    queryFn: () => api<{ models: PickerModel[] }>("/models"),
  });
  const settings = useSettingsQuery(true);
  const toolsInfo = useQuery({
    queryKey: ["tools"],
    queryFn: () => api<{ runnerOnline: boolean }>("/tools"),
    refetchInterval: 30_000,
  });
  const conv = useQuery({
    queryKey: ["conversation", id],
    queryFn: () => api<{ conversation: Conversation; messages: Message[] }>(`/conversations/${id}`),
    enabled: !!id,
  });
  const c = conv.data?.conversation;
  const chatLive = useChatStore((s) => (id ? s.streams[id] : undefined));
  const agentLive = useAgentStore((s) => (id ? s.live[id] : undefined));
  const isAgent = id ? (c ? c.mode === "agent" || agentMode : agentMode) : agentMode;
  const running = !!chatLive?.running || !!agentLive?.running;

  // A run that survived a page reload: re-attach to its event stream, or offer to resume if the host lost it.
  const orphan = !!id && c?.runStatus === "running" && !agentLive;
  const status = useQuery({
    queryKey: ["agent-status", id, c?.runStatus],
    queryFn: () => api<{ running: boolean; seq: number }>(`/conversations/${id}/agent/status`),
    enabled: orphan,
  });
  useEffect(() => {
    if (orphan && status.data?.running && id) void follow(id, qc, 0);
  }, [orphan, status.data?.running, id, qc]);
  const interrupted = orphan && status.data && !status.data.running;

  const list = models.data?.models ?? [];
  const selected = useMemo(() => {
    const find = (r?: { providerId: string | null; model: string | null } | null) =>
      r?.providerId && r.model
        ? list.find((m) => m.providerId === r.providerId && m.model === r.model)
        : undefined;
    return find(c) ?? find(lastModel) ?? find(settings.data?.settings.defaultModel) ?? list[0];
  }, [list, c, lastModel, settings.data?.settings.defaultModel]);

  const modelRef = selected
    ? { providerId: selected.providerId, model: selected.model }
    : undefined;
  const thinkBody = think && selected?.capabilities.thinking ? { thinking: true } : {};

  const pick = (m: PickerModel) => {
    set({ lastModel: { providerId: m.providerId, model: m.model } });
    if (id)
      api(`/conversations/${id}`, {
        method: "PATCH",
        body: { model: { providerId: m.providerId, model: m.model } },
      }).then(() => qc.invalidateQueries({ queryKey: ["conversation", id] }));
  };
  const createConv = useMutation({
    mutationFn: () =>
      api<{ conversation: Conversation }>("/conversations", {
        method: "POST",
        body: {
          ...(modelRef ? { model: modelRef } : {}),
          mode: isAgent ? "agent" : "chat",
          agentId: lastAgentId,
          projectId: newProjectId,
        },
      }),
  });

  const dispatch = (
    convId: string,
    extra: Record<string, unknown>,
    pendingUser?: string,
    model = modelRef,
  ) => {
    // Auto: look at the message and choose what fits (quick chat → fast model, hard / code → strongest, images → vision,
    // documents / connected accounts / running code → agent mode with a tool-capable model).
    const text = typeof extra.content === "string" ? extra.content : "";
    const attached = Array.isArray(extra.attachments) && extra.attachments.length > 0;
    const lastAssistant = textOf(
      [...messages].reverse().find((m) => m.role === "assistant")?.content ?? [],
    );
    const cls = classifyRequest(text, { hasImages: attached, lastAssistant });
    const needsAgent = !isAgent && cls.agent;
    if (autoModel && text && !extra.regenerate && extra.intent !== "image") {
      const ranked = rankForRequest(
        list.map((m) => ({
          providerId: m.providerId,
          model: m.model,
          strength: m.strength,
          speed: m.speed ?? 3,
          cost: m.cost,
          capabilities: m.capabilities,
          src: m,
        })),
        { ...cls, agent: cls.agent || isAgent },
      );
      const best = ranked[0];
      if (best) {
        extra = { ...extra, level: cls.level };
        model = { providerId: best.providerId, model: best.model };
        set({
          lastAuto: {
            label: best.src.label,
            provider: best.src.providerName,
            reason: cls.agent || isAgent ? "tools" : cls.reason,
          },
        });
      }
    } else if (needsAgent) {
      // Manual model but the request needs tools: use a model that can call them.
      const cur = list.find((m) => m.providerId === model?.providerId && m.model === model?.model);
      if (!cur?.capabilities.tools) {
        const best = [...list]
          .filter((m) => m.capabilities.tools)
          .sort((a, b) => b.strength - a.strength)[0];
        if (best) model = { providerId: best.providerId, model: best.model };
      }
    }
    if (isAgent || needsAgent)
      void startAgent(
        convId,
        { ...extra, ...thinkBody, planMode: plan, ...(model ? { model } : {}) },
        qc,
        pendingUser,
      );
    else
      void runTurn(
        convId,
        {
          ...extra,
          ...thinkBody,
          ...(voiceOpen ? { voice: true } : {}),
          ...(model ? { model } : {}),
        },
        qc,
        pendingUser,
      );
  };
  // Full-duplex live voice when the user has an OpenAI key (and has not switched it off).
  const useRealtime =
    !!voiceCaps.data?.realtime && settings.data?.settings.voice?.realtime !== false;
  const send = async (text: string, attachments: string[] = []) => {
    speaker.current?.cancel();
    if (usePrefs.getState().readAloud) unlockAudio();
    let convId = id;
    if (!convId) {
      convId = (await createConv.mutateAsync()).conversation.id;
      await qc.invalidateQueries({ queryKey: ["conversations"] });
      navigate(`/c/${convId}`, { replace: true });
    }
    // "/image …" (the composer's Image button, or typed) forces image generation for this message.
    const prefix = /^\/(?:image|img|صورة)\s+/i.exec(text);
    const forced = prefix || sendMode === "image";
    const content = prefix ? text.slice(prefix[0].length) : text;
    dispatch(
      convId,
      {
        content,
        ...(forced ? { intent: "image" } : sendMode === "text" ? { intent: "text" } : {}),
        ...(attachments.length ? { attachments } : {}),
      },
      content,
    );
  };
  const regenerate = (m?: PickerModel) => {
    if (!id) return;
    if (m) set({ lastModel: { providerId: m.providerId, model: m.model } });
    dispatch(
      id,
      { regenerate: true },
      undefined,
      m ? { providerId: m.providerId, model: m.model } : modelRef,
    );
  };
  const edit = (messageId: string, text: string) =>
    id && dispatch(id, { editMessageId: messageId, content: text }, text);
  const stop = () => {
    if (!id) return;
    if (agentLive?.running) void cancelRun(id);
    else stopStream(id);
  };

  // ---- Derived transcript data ----
  const messages = conv.data?.messages ?? [];
  const tools: ToolInfo = useMemo(() => {
    const results = new Map<string, { content: string; isError: boolean }>();
    for (const m of messages)
      if (m.role === "user")
        for (const p of m.content)
          if (p.type === "tool_result")
            results.set(p.toolCallId, { content: p.content, isError: !!p.isError });
    return { results, live: agentLive?.toolStates ?? {}, subs: agentLive?.subagents };
  }, [messages, agentLive?.toolStates, agentLive?.subagents]);
  const visible = messages.filter((m) => !isToolResultOnly(m));
  const pending = c?.runStatus === "awaiting_approval" ? c.pending : null;
  const tasks = agentLive?.tasks ?? c?.tasks ?? [];
  const live = chatLive
    ? chatLive
    : agentLive
      ? {
          text: agentLive.text,
          thinking: agentLive.thinking,
          fallbacks: agentLive.fallbacks,
          error: agentLive.error,
          running: agentLive.running,
          answeredBy: agentLive.answeredBy && { providerId: "", ...agentLive.answeredBy },
          pendingUser: agentLive.pendingUser,
        }
      : undefined;
  const lastText = (m?: Message) =>
    m?.content.some((p) => p.type === "text" && p.text === live?.pendingUser);
  const pendingUser =
    live?.pendingUser && !(visible.at(-1)?.role === "user" && lastText(visible.at(-1)))
      ? live.pendingUser
      : undefined;
  const showLive = !!live && (live.running || live.error || live.text);

  // ---- Scrolling: stick to bottom unless the user scrolled up ----
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const onScroll = () => {
    const el = scroller.current;
    if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on any content change
  useEffect(() => {
    if (stick.current && scroller.current)
      scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages.length, live?.text, live?.thinking, pendingUser, pending?.length]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: new conversation => jump to bottom
  useEffect(() => {
    stick.current = true;
  }, [id]);

  const starters = useQuery({
    queryKey: ["starters"],
    queryFn: () => api<{ items: string[] }>("/suggest/starters"),
    staleTime: 30_000,
    enabled: !id && suggestionsOn,
  });
  const defaults = [t("suggest.1"), t("suggest.2"), t("suggest.3"), t("suggest.4")];
  const fromHistory = starters.data?.items ?? [];
  const startersOn = suggestionsOn && settings.data?.settings.suggestStarters !== false;
  // Your own recent prompts first; the built-in ideas only fill the gaps (and are hidden if you turn starters off).
  const suggestions = startersOn ? [...fromHistory, ...defaults].slice(0, 4) : [];
  const hasModels = list.length > 0;
  const lastIdx = visible.length - 1;
  const ratings = useQuery({
    queryKey: ["ratings", id],
    queryFn: () => api<{ ratings: Record<string, number> }>(`/feedback/mine?conversationId=${id}`),
    enabled: !!id,
  });
  const rate = useMutation({
    mutationFn: async (a: { id: string; v: 1 | -1 | 0; reason?: string }) =>
      a.v === 0
        ? api(`/feedback/${a.id}`, { method: "DELETE" })
        : api("/feedback", {
            method: "POST",
            body: { messageId: a.id, rating: a.v, reason: a.reason },
          }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ratings", id] }),
  });
  const doneRef = useRef(false);
  const [announce, setAnnounce] = useState("");
  // biome-ignore lint/correctness/useExhaustiveDependencies: announce once when a run ends
  useEffect(() => {
    if (running) doneRef.current = true;
    else if (doneRef.current) {
      doneRef.current = false;
      setAnnounce(t("a11y.replyDone"));
      const h = setTimeout(() => setAnnounce(""), 2500);
      return () => clearTimeout(h);
    }
  }, [running]);
  const me = qc.getQueryData<{ user?: { name?: string } }>(["me"]);
  const firstName = (me?.user?.name ?? "").trim().split(/\s+/)[0] ?? "";
  // What the user wrote lately: decides the Arabic dialect a message is read aloud in.
  const userSample = messages
    .filter((m) => m.role === "user")
    .slice(-4)
    .map((m) => textOf(m.content))
    .join(" ");

  // Text in, voice out: when a reply finishes, read it aloud in its language and the user's dialect.
  const readAloud = usePrefs((st) => st.readAloud);
  const speaker = useRef<Speaker | null>(null);
  const toSpeak = useRef(false);
  const spokenId = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on the falling edge of `running` and when messages refresh
  useEffect(() => {
    if (running) {
      toSpeak.current = readAloud && !voiceOpen;
      return;
    }
    if (!toSpeak.current) return;
    const last = [...messages].reverse().find((m) => m.role === "assistant");
    const text = last ? textOf(last.content) : "";
    if (!last || !text || last.id === spokenId.current) return;
    toSpeak.current = false;
    spokenId.current = last.id;
    speaker.current?.cancel();
    const voice = settings.data?.settings.voice ?? DEFAULT_VOICE;
    const sp = createSpeaker({ ...voice.tts, auto: true }, undefined, userSample);
    speaker.current = sp;
    for (const c of chunkForSpeech(text)) sp.speak(c);
  }, [running, messages.length]);
  useEffect(() => () => speaker.current?.cancel(), []);

  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        <header
          data-shell="chat-header"
          className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--line)] px-3"
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={() => set({ sidebarOpen: !sidebarOpen })}
            aria-label={t("sidebar.toggle")}
            className={sidebarOpen ? "lg:hidden" : ""}
          >
            <PanelLeft className="size-5 rtl:-scale-x-100" />
          </Button>
          {agentOn && (
            <AgentPicker
              value={c ? c.agentId : lastAgentId}
              onPick={(aid) => {
                set({ lastAgentId: aid });
                if (id)
                  void api(`/conversations/${id}`, {
                    method: "PATCH",
                    body: { agentId: aid },
                  }).then(() => qc.invalidateQueries({ queryKey: ["conversation", id] }));
              }}
            />
          )}
          {agentOn && <ModePicker />}
          <ModelPicker models={list} value={selected} onPick={pick} />
          <span
            className="ms-2 hidden min-w-0 flex-1 truncate text-sm text-[var(--fg-muted)] md:block"
            dir="auto"
          >
            {c?.title}
          </span>
          {id && messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="ms-auto md:ms-0"
              aria-label={t("export.title")}
              title={t("export.title")}
              onClick={() => {
                const title = c?.title ?? "chat";
                const text = conversationToText(title, messages, "md", {
                  user: t("export.you"),
                  assistant: "AI",
                });
                downloadText(`${safeFilename(title)}.md`, text, "text/markdown");
              }}
            >
              <Download className="size-5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className={id && messages.length > 0 ? "" : "ms-auto md:ms-0"}
            onClick={() => navigate("/")}
            aria-label={t("chat.new")}
          >
            <Plus className="size-5" />
          </Button>
        </header>

        <div role="status" aria-live="polite" className="sr-only">
          {announce}
        </div>
        <div ref={scroller} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto">
          {id && conv.isLoading ? (
            <div className="grid h-full place-items-center">
              <Spinner />
            </div>
          ) : id && conv.isError ? (
            <EmptyState
              title={t("chat.notFound")}
              action={<Button onClick={() => navigate("/")}>{t("chat.new")}</Button>}
            />
          ) : visible.length === 0 && !showLive && !pendingUser ? (
            <div className="mx-auto flex h-full max-w-2xl flex-col items-center justify-center gap-6 px-4 py-10 text-center">
              <Logo size={64} />
              <div>
                <p className="mb-1 text-sm text-[var(--fg-muted)]" dir="auto">
                  {t(greetKey(new Date().getHours()), { name: firstName })}
                </p>
                <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                  {t("empty.title")}
                </h1>
                <p className="mt-2 text-[var(--fg-muted)]">{t("empty.subtitle")}</p>
                <p className="mt-1 text-xs tracking-widest text-[var(--fg-faint)]">
                  AI · by QN Ventures
                </p>
              </div>
              {!hasModels && !models.isLoading ? (
                <div className="glass w-full rounded-[var(--radius)] border border-[var(--line)] p-5 text-start">
                  <h2 className="font-display text-lg font-semibold">{t("empty.noModelTitle")}</h2>
                  <p className="mb-4 mt-1 text-sm text-[var(--fg-muted)]">
                    {t("empty.noModelBody")}
                  </p>
                  <Link to="/settings/models">
                    <Button variant="primary">{t("empty.addModel")}</Button>
                  </Link>
                </div>
              ) : (
                <div className="grid w-full gap-2 sm:grid-cols-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      dir="auto"
                      onClick={() => setPrefill({ text: s, nonce: Date.now() })}
                      className="rounded-xl border border-[var(--line)] bg-[var(--surface)] p-3.5 text-start text-sm text-[var(--fg-muted)] transition hover:border-[var(--accent-ring)] hover:bg-[var(--surface-2)] hover:text-[var(--fg)]"
                    >
                      {s}
                    </button>
                  ))}
                  {fromHistory.length > 0 && startersOn && (
                    <p className="text-xs text-[var(--fg-faint)] sm:col-span-2">
                      {t("suggest.fromHistory")}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="mx-auto flex w-full max-w-[var(--chat-width)] flex-col gap-6 px-3 py-6 sm:px-4">
              {visible.map((m, i) =>
                m.role === "user" ? (
                  <UserMessage key={m.id} m={m} onEdit={edit} busy={running} />
                ) : (
                  <AssistantMessage
                    key={m.id}
                    m={m}
                    isLast={i === lastIdx}
                    busy={running}
                    models={list}
                    onRegenerate={regenerate}
                    tools={tools}
                    voice={settings.data?.settings.voice ?? DEFAULT_VOICE}
                    userSample={userSample}
                    onFollowUp={(prompt) => void send(prompt)}
                    rating={ratings.data?.ratings[m.id]}
                    onRate={(v, reason) => rate.mutate({ id: m.id, v, reason })}
                  />
                ),
              )}
              {pendingUser && (
                <div className="flex justify-end">
                  <div
                    dir="auto"
                    className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-ee-md bg-[var(--surface-3)] px-4 py-2.5"
                  >
                    {pendingUser}
                  </div>
                </div>
              )}
              {showLive && live && (
                <LiveAssistantMessage s={{ ...live, answeredBy: live.answeredBy as never }} />
              )}
              {interrupted && (
                <div className="flex items-center gap-3 rounded-xl border border-[var(--warn)]/40 bg-[color-mix(in_oklab,var(--warn)_8%,transparent)] p-3 text-sm">
                  <span className="flex-1">{t("agent.interrupted")}</span>
                  <Button size="sm" variant="primary" onClick={() => id && void resume(id, qc)}>
                    <RotateCcw className="size-4" /> {t("agent.resume")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {pending && id && !running && (
          <ApprovalBar
            calls={pending}
            busy={running}
            onDecide={(d, always) => void decide(id, d, always, qc)}
          />
        )}
        {pending && id && running && agentLive && (
          <div className="mx-auto mb-2 text-xs text-[var(--fg-muted)]">{t("agent.working")}</div>
        )}
        <TaskPanel tasks={tasks} />
        <Composer
          running={running}
          disabled={!hasModels || createConv.isPending || !!pending}
          onSend={send}
          onStop={stop}
          canThink={!!selected?.capabilities.thinking}
          think={think}
          onThink={setThink}
          agent={isAgent}
          onAgent={(v) => set({ agentMode: v })}
          plan={plan || !!c?.planMode}
          onPlan={(v) => {
            setPlan(v);
            if (id) void api(`/conversations/${id}`, { method: "PATCH", body: { planMode: v } });
          }}
          runnerOnline={toolsInfo.data?.runnerOnline}
          voice={settings.data?.settings.voice ?? DEFAULT_VOICE}
          onVoiceMode={() => {
            unlockAudio();
            setVoiceOpen(true);
          }}
          prefill={prefill}
          autoFocusKey={id ?? "new"}
        />
      </div>
      {artifactId && <ArtifactPanel id={artifactId} />}
      {voiceOpen && useRealtime && <RealtimeVoice onClose={() => setVoiceOpen(false)} />}
      {voiceOpen && !useRealtime && (
        <VoiceMode
          cfg={settings.data?.settings.voice ?? DEFAULT_VOICE}
          assistantText={agentLive?.text || chatLive?.text || ""}
          assistantRunning={running}
          onSend={(text) => void send(text)}
          onStopRun={stop}
          onClose={() => setVoiceOpen(false)}
        />
      )}
    </div>
  );
}
